import {
  DocumentPermissionFlag,
  DocumentVersionStatus,
  Permission,
  type DocumentListFilter,
} from '@qualicloud/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DocumentStatusBadge, DaysRemaining, ValiditySemaphore } from '@/components/validity-semaphore';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconClipboardCheck,
  IconDownload,
  IconEdit,
  IconEye,
  IconFiles,
  IconFilter,
  IconPlus,
  IconPrinter,
  IconSearch,
  Input,
  NumberInput,
  Pagination,
  Popover,
  Select,
  Skeleton,
  Table,
  TableActions,
  TreeView,
  cn,
  focusRing,
  useToast,
  type SortState,
  type TableColumn,
  type TreeNode,
} from '@/design-system';
import { documentTypesApi, documentsApi, queryKeys, saveBlob, toUserMessage } from '@/lib/api';
import type { DocumentStatusCounts, DocumentTypeNode, MasterListRow } from '@/lib/api/types';
import { formatDateOnly } from '@/lib/format/datetime';
import { DOCUMENT_STATUS_LABEL } from '@/lib/i18n';
import { useAuth } from '@/providers/auth-provider';
import type { MasterListSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

type SortKey = NonNullable<DocumentListFilter['sortBy']>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LISTA MESTRA — a tela principal do GED
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * COBERTURA DO LEGADO (ged.md §14) E O QUE FOI CORRIGIDO
 *
 * Colunas do legado (`ged_list.twig:56-65`): ID, Versão, Data Versão, Nome
 * Documento, Tipo, "Valido até" (sic) e Ação. Todas presentes aqui, mais
 * "Dias restantes" e "Situação", que existiam nos dados e não eram exibidas.
 *
 * · ORDENAÇÃO: o legado tinha `ORDER BY tipo` FIXO, e as URLs de ordenação
 *   apontavam para colunas de outro módulo (resíduo). Aqui toda coluna
 *   relevante é ordenável e o estado vai para a URL.
 * · PAGINAÇÃO: o legado retornava TODOS os registros. Aqui é paginado no
 *   servidor.
 * · FILTRO POR TIPO: o legado casava por NOME do tipo, com interpolação SQL sem
 *   escape (BUG-22) e sem hierarquia. Aqui é por ID, com opção de incluir
 *   subtipos, resolvida por closure table no servidor.
 * · FILTROS COMBINÁVEIS: no legado, tipo e situação eram MUTUAMENTE EXCLUSIVOS
 *   (clicar num zerava o outro) e os contadores ignoravam os dois (BUG-29).
 *   Aqui todos os filtros combinam.
 * · AÇÕES: o legado renderizava Download e Editar incondicionalmente ao filtrar
 *   por pasta, ignorando as flags (BUG-60). Aqui cada ação é condicionada à
 *   flag efetiva daquela linha, calculada no servidor.
 * · SEMÁFORO: cores preservadas, mas nunca sozinhas — ver `<ValiditySemaphore>`.
 */
export function MasterListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();
  const search = useSearch({ strict: false }) as unknown as Partial<DocumentListFilter>;

  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const filter: Partial<DocumentListFilter> = useMemo(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? 50,
      documentTypeId: search.documentTypeId,
      includeDescendantTypes: search.includeDescendantTypes ?? true,
      status: search.status,
      search: search.search,
      expiringWithinDays: search.expiringWithinDays,
      onlyPendingMyApproval: search.onlyPendingMyApproval ?? false,
      onlyPendingMyAcknowledgement: search.onlyPendingMyAcknowledgement ?? false,
      sortBy: search.sortBy ?? 'documentType',
      sortDir: search.sortDir ?? 'asc',
    }),
    [search],
  );

  const updateFilter = useCallback(
    (patch: Partial<DocumentListFilter>) => {
      void navigate({
        to: '/documentos',
        // Qualquer mudança de filtro volta para a página 1 — senão o usuário
        // filtra e cai numa página vazia sem entender por quê.
        search: { ...filter, ...patch, page: patch.page ?? 1 } as MasterListSearch,
      });
    },
    [filter, navigate],
  );

  const typesQuery = useQuery({
    queryKey: queryKeys.documentTypes.tree(),
    queryFn: () => documentTypesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const listQuery = useQuery({
    queryKey: queryKeys.documents.list(filter),
    queryFn: () => documentsApi.list(filter),
    placeholderData: keepPreviousData,
  });

  /**
   * Contadores por situação — `countDoc()` do legado (ged.md §14.4).
   *
   * O filtro de SITUAÇÃO é deliberadamente removido da consulta: o contador de
   * cada chip precisa responder "quantos existem nesta situação com os demais
   * filtros aplicados". Mantê-lo zeraria todas as chips menos a selecionada.
   * Os outros filtros permanecem — o legado ignorava todos (BUG-29) e por isso
   * seus contadores nunca correspondiam à lista exibida.
   */
  const countsFilter = useMemo(() => {
    const {
      status: _status,
      page: _page,
      pageSize: _pageSize,
      // Ordenação e paginação não afetam contagem; mandá-las só sujaria a
      // chave de cache e faria o contador recarregar ao trocar de coluna.
      sortBy: _sortBy,
      sortDir: _sortDir,
      ...rest
    } = filter;
    return rest;
  }, [filter]);

  const countsQuery = useQuery({
    queryKey: queryKeys.documents.counts(countsFilter as Record<string, unknown>),
    queryFn: () => documentsApi.counts(countsFilter),
    placeholderData: keepPreviousData,
  });

  const treeNodes = useMemo(() => toTreeNodes(typesQuery.data ?? []), [typesQuery.data]);
  const totalDocuments = useMemo(
    () => (typesQuery.data ?? []).reduce((acc, node) => acc + countDocuments(node), 0),
    [typesQuery.data],
  );

  const handleExport = async (format: 'csv' | 'pdf') => {
    setExporting(format);
    try {
      const file = await documentsApi.exportMasterList(filter, format);
      saveBlob(file.blob, file.fileName);
      toast({
        tone: 'success',
        title: 'Exportação concluída',
        description:
          'O arquivo usa exatamente os mesmos filtros e as mesmas permissões da tela. A exportação foi registrada na trilha de auditoria.',
      });
    } catch (error) {
      toast({
        tone: 'error',
        title: 'Não foi possível exportar',
        description: toUserMessage(error),
      });
    } finally {
      setExporting(null);
    }
  };

  const columns = useMemo<Array<TableColumn<MasterListRow, SortKey>>>(
    () => [
      {
        id: 'name',
        header: 'Nome do documento',
        sortKey: 'name',
        width: '30%',
        cell: (row) => (
          <div className="min-w-0">
            <Link
              to="/documentos/$documentId"
              params={{ documentId: row.documentId }}
              className="block truncate font-medium text-brand hover:underline"
            >
              {row.code ? <span className="font-mono text-xs">{row.code} · </span> : null}
              {row.name}
            </Link>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {row.pendingMyApproval ? (
                <Badge tone="warning" size="sm" icon={<IconClipboardCheck size={11} />}>
                  Aguarda sua aprovação
                </Badge>
              ) : null}
              {row.acknowledgementStatus === 'PENDING' ? (
                <Badge tone="info" size="sm" icon={<IconEye size={11} />}>
                  Aguarda sua ciência
                </Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'documentType',
        header: 'Tipo',
        sortKey: 'documentType',
        width: '15%',
        cell: (row) => <span className="text-content-muted">{row.documentType}</span>,
      },
      {
        id: 'version',
        header: 'Versão',
        sortKey: 'version',
        width: '7%',
        align: 'right',
        cell: (row) => <span className="qc-tabular">{row.version}</span>,
      },
      {
        id: 'revisionDate',
        header: 'Data da versão',
        sortKey: 'revisionDate',
        width: '11%',
        cell: (row) => <span className="qc-tabular">{formatDateOnly(row.revisionDate)}</span>,
      },
      {
        id: 'validUntil',
        header: 'Válido até',
        sortKey: 'validUntil',
        width: '13%',
        cell: (row) => (
          <ValiditySemaphore
            state={row.validityState}
            daysUntilExpiry={row.daysUntilExpiry}
            validUntil={row.validUntil}
          />
        ),
      },
      {
        id: 'daysUntilExpiry',
        header: 'Dias',
        srHeader: 'Dias restantes até o vencimento',
        width: '6%',
        align: 'right',
        cell: (row) => <DaysRemaining days={row.daysUntilExpiry} state={row.validityState} />,
      },
      {
        id: 'status',
        header: 'Situação',
        sortKey: 'status',
        width: '10%',
        cell: (row) => <DocumentStatusBadge status={row.status} />,
      },
      {
        id: 'actions',
        header: 'Ações',
        width: '8%',
        align: 'right',
        cell: (row) => <RowActions row={row} />,
      },
    ],
    [],
  );

  const activeFilterCount = countActiveFilters(filter);

  return (
    <>
      <PageHeader
        title="Lista Mestra de Documentos"
        description="Todos os documentos controlados que você tem permissão para ver. A visibilidade e as ações disponíveis em cada linha são resolvidas pelo servidor a partir das regras de acesso do documento."
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={<IconDownload size={15} />}
              loading={exporting === 'csv'}
              onClick={() => void handleExport('csv')}
            >
              Exportar CSV
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<IconPrinter size={15} />}
              loading={exporting === 'pdf'}
              onClick={() => void handleExport('pdf')}
            >
              Exportar PDF
            </Button>
            {can(Permission.DOCUMENT_CREATE) ? (
              <Button asChild variant="primary" leadingIcon={<IconPlus size={15} />}>
                <Link to="/documentos/novo">Novo documento</Link>
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody>
        <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
          {/* ── Árvore de tipos ─────────────────────────────────────────── */}
          <aside aria-labelledby="type-tree-heading" className="min-w-0">
            <Card padding="sm" className="lg:sticky lg:top-20">
              <h2
                id="type-tree-heading"
                className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-content-muted"
              >
                Tipos de documento
              </h2>

              {typesQuery.isPending ? (
                <div className="flex flex-col gap-1.5 p-1">
                  {Array.from({ length: 8 }, (_, index) => (
                    <Skeleton key={index} className="h-7 w-full" />
                  ))}
                </div>
              ) : (
                <TreeView
                  ariaLabel="Tipos de documento"
                  nodes={treeNodes}
                  selectedId={filter.documentTypeId ?? null}
                  rootNode={{ label: 'Todos os tipos', count: totalDocuments }}
                  onSelect={(node) =>
                    updateFilter({ documentTypeId: node?.id as string | undefined })
                  }
                />
              )}

              <div className="mt-3 border-t border-border-subtle pt-3">
                <Checkbox
                  checked={filter.includeDescendantTypes ?? true}
                  onCheckedChange={(checked) => updateFilter({ includeDescendantTypes: checked })}
                  label="Incluir subtipos"
                  description="Traz também os documentos dos tipos filhos do tipo selecionado."
                />
              </div>
            </Card>
          </aside>

          {/* ── Tabela ──────────────────────────────────────────────────── */}
          <div className="min-w-0">
            <FilterBar
              filter={filter}
              activeCount={activeFilterCount}
              onChange={updateFilter}
              onClear={() =>
                void navigate({ to: '/documentos', search: { page: 1, pageSize: filter.pageSize } })
              }
            />

            <StatusCounters
              counts={countsQuery.data}
              loading={countsQuery.isPending}
              selected={filter.status}
              onSelect={(status) => updateFilter({ status })}
            />

            <div className="mt-3">
              <Table<MasterListRow, SortKey>
                caption="Lista Mestra de documentos controlados. Colunas: nome, tipo, versão, data da versão, validade, dias restantes, situação e ações."
                columns={columns}
                rows={listQuery.data?.items ?? []}
                rowKey={(row) => row.documentId}
                loading={listQuery.isPending}
                sort={{
                  by: (filter.sortBy ?? 'documentType') as SortKey,
                  dir: filter.sortDir ?? 'asc',
                }}
                onSortChange={(sort: SortState<SortKey>) =>
                  updateFilter({ sortBy: sort.by, sortDir: sort.dir })
                }
                rowClassName={(row) =>
                  row.status === DocumentVersionStatus.EXPIRED ? 'bg-danger-subtle/30' : undefined
                }
                empty={
                  <EmptyState
                    icon={<IconFiles size={22} />}
                    title={
                      activeFilterCount > 0
                        ? 'Nenhum documento corresponde aos filtros'
                        : 'Nenhum documento visível para você'
                    }
                    description={
                      activeFilterCount > 0
                        ? 'Ajuste ou limpe os filtros para ampliar a busca. Lembre-se de que a Lista Mestra só mostra documentos aos quais você tem ao menos uma permissão.'
                        : 'Um documento só aparece aqui se alguma regra de acesso conceder a você pelo menos uma das seis permissões. Se você deveria ver documentos, peça ao gestor da qualidade para revisar a distribuição.'
                    }
                    action={
                      activeFilterCount > 0 ? (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            void navigate({ to: '/documentos', search: { page: 1 } })
                          }
                        >
                          Limpar filtros
                        </Button>
                      ) : can(Permission.DOCUMENT_CREATE) ? (
                        <Button asChild variant="primary">
                          <Link to="/documentos/novo">Criar o primeiro documento</Link>
                        </Button>
                      ) : undefined
                    }
                  />
                }
              />

              {listQuery.data && listQuery.data.total > 0 ? (
                <div className="rounded-b-lg border-x border-b border-border bg-surface">
                  <Pagination
                    page={listQuery.data.page}
                    pageSize={listQuery.data.pageSize}
                    total={listQuery.data.total}
                    itemNoun="documentos"
                    onPageChange={(page) => updateFilter({ page })}
                    onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </PageBody>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Barra de filtros
// ─────────────────────────────────────────────────────────────────────────────

function FilterBar({
  filter,
  activeCount,
  onChange,
  onClear,
}: {
  readonly filter: Partial<DocumentListFilter>;
  readonly activeCount: number;
  readonly onChange: (patch: Partial<DocumentListFilter>) => void;
  readonly onClear: () => void;
}) {
  const [term, setTerm] = useState(filter.search ?? '');

  return (
    <section aria-label="Filtros da Lista Mestra" className="flex flex-wrap items-end gap-3">
      <form
        role="search"
        className="min-w-[16rem] flex-1"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ search: term.trim() || undefined });
        }}
      >
        <label htmlFor="master-list-search" className="qc-sr-only">
          Buscar por nome ou código do documento
        </label>
        <Input
          id="master-list-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar por nome ou código…"
          leadingIcon={<IconSearch size={15} />}
        />
      </form>

      <div className="w-48">
        <label htmlFor="master-list-status" className="qc-sr-only">
          Filtrar por situação
        </label>
        <Select
          ariaLabel="Filtrar por situação"
          value={filter.status?.[0] ?? '__all__'}
          onValueChange={(value) =>
            onChange({
              status:
                value === '__all__' ? undefined : [value as DocumentVersionStatus],
            })
          }
          options={[
            { value: '__all__', label: 'Todas as situações' },
            ...Object.values(DocumentVersionStatus).map((status) => ({
              value: status,
              label: DOCUMENT_STATUS_LABEL[status],
            })),
          ]}
        />
      </div>

      <Popover
        width="20rem"
        title="Filtros avançados"
        showClose
        trigger={
          <Button variant="secondary" leadingIcon={<IconFilter size={15} />}>
            Mais filtros
            {activeCount > 0 ? (
              <Badge tone="brand" size="sm" className="ml-1">
                {activeCount}
              </Badge>
            ) : null}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <Field
            label="Vencendo em até (dias)"
            description="Mostra apenas documentos cuja validade termina dentro do prazo informado."
          >
            <NumberInput
              min={0}
              max={365}
              value={filter.expiringWithinDays ?? ''}
              onChange={(event) =>
                onChange({
                  expiringWithinDays: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                })
              }
              className="w-28"
            />
          </Field>

          <Checkbox
            checked={filter.onlyPendingMyApproval ?? false}
            onCheckedChange={(checked) =>
              onChange({ onlyPendingMyApproval: checked || undefined })
            }
            label="Aguardando minha aprovação"
            description="Apenas versões em que você é aprovador designado e ainda não decidiu."
          />

          <Checkbox
            checked={filter.onlyPendingMyAcknowledgement ?? false}
            onCheckedChange={(checked) =>
              onChange({ onlyPendingMyAcknowledgement: checked || undefined })
            }
            label="Aguardando minha ciência"
            description="Apenas documentos distribuídos a você que exigem confirmação de leitura."
          />

          {activeCount > 0 ? (
            <Button variant="ghost" onClick={onClear} className="self-start">
              Limpar todos os filtros
            </Button>
          ) : null}
        </div>
      </Popover>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Contadores por situação
// ─────────────────────────────────────────────────────────────────────────────

/**
 * As cinco situações que o contador lateral do legado exibia (ged.md §14.4):
 * `Aprovado-N`, `Pendente de Aprovação-N`, `Reprovado-N`, `Vencido-N`,
 * `Obsoletado-N`. `DRAFT` e `SUPERSEDED` não existiam lá e só aparecem quando
 * há algo para mostrar — chip permanentemente em zero é ruído.
 */
const LEGACY_COUNTER_STATUSES: readonly DocumentVersionStatus[] = [
  DocumentVersionStatus.APPROVED,
  DocumentVersionStatus.IN_REVIEW,
  DocumentVersionStatus.REJECTED,
  DocumentVersionStatus.EXPIRED,
  DocumentVersionStatus.OBSOLETE,
];

const EXTRA_COUNTER_STATUSES: readonly DocumentVersionStatus[] = [
  DocumentVersionStatus.DRAFT,
  DocumentVersionStatus.SUPERSEDED,
];

/**
 * Contadores por situação, filtráveis.
 *
 * Substitui a árvore "Situação" do legado. Duas correções em relação a ela:
 *  · os contadores respeitam os demais filtros ativos (o legado os ignorava —
 *    BUG-29 — e mostrava números que não batiam com a lista);
 *  · situação e tipo COMBINAM. No legado eram mutuamente exclusivos: clicar
 *    numa situação zerava o filtro de pasta e vice-versa (ged.md §14.4).
 *
 * ACESSIBILIDADE: cada chip é um botão com `aria-pressed`, dentro de um grupo
 * rotulado. O estado selecionado não é só a cor de fundo — o `aria-pressed` diz
 * ao leitor de tela, e a marca lateral esquerda dá o reforço visual em preto e
 * branco. A contagem é lida junto com o rótulo, não como número solto.
 */
function StatusCounters({
  counts,
  loading,
  selected,
  onSelect,
}: {
  readonly counts: DocumentStatusCounts | undefined;
  readonly loading: boolean;
  readonly selected: readonly DocumentVersionStatus[] | undefined;
  readonly onSelect: (status: DocumentVersionStatus[] | undefined) => void;
}) {
  const visibleStatuses = useMemo(() => {
    const extras = EXTRA_COUNTER_STATUSES.filter(
      (status) => (counts?.byStatus[status] ?? 0) > 0 || selected?.includes(status),
    );
    return [...LEGACY_COUNTER_STATUSES, ...extras];
  }, [counts, selected]);

  if (loading) {
    return (
      <div className="mt-3 flex flex-wrap gap-2" aria-hidden="true">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-8 w-32 rounded-full" />
        ))}
      </div>
    );
  }

  if (!counts) {
    return (
      <p className="mt-3 text-sm text-content-muted">
        Não foi possível carregar os contadores por situação agora. A lista abaixo continua correta;
        use o seletor de situação para filtrar.
      </p>
    );
  }

  const noneSelected = !selected || selected.length === 0;

  return (
    <div
      role="group"
      aria-label="Contadores por situação. Selecione para filtrar a lista."
      className="mt-3 flex flex-wrap items-center gap-2"
    >
      <CounterChip
        label="Todas as situações"
        count={counts.total}
        pressed={noneSelected}
        onClick={() => onSelect(undefined)}
      />
      {visibleStatuses.map((status) => (
        <CounterChip
          key={status}
          label={DOCUMENT_STATUS_LABEL[status]}
          count={counts.byStatus[status] ?? 0}
          pressed={selected?.includes(status) ?? false}
          onClick={() => onSelect(selected?.includes(status) ? undefined : [status])}
        />
      ))}
    </div>
  );
}

function CounterChip({
  label,
  count,
  pressed,
  onClick,
}: {
  readonly label: string;
  readonly count: number;
  readonly pressed: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border py-1 pl-3 pr-2 text-sm transition-colors',
        focusRing,
        pressed
          ? 'border-brand bg-brand-subtle font-semibold text-brand'
          : 'border-border bg-surface text-content-muted hover:bg-surface-sunken hover:text-content',
      )}
    >
      {/* Marca de seleção que não depende de cor — WCAG 1.4.1. */}
      <span aria-hidden="true" className={cn('text-xs', pressed ? 'opacity-100' : 'opacity-0')}>
        ✓
      </span>
      <span>{label}</span>
      <span
        className={cn(
          'rounded-full px-1.5 py-0.5 text-xs qc-tabular',
          pressed ? 'bg-brand text-brand-contrast' : 'bg-surface-sunken text-content-muted',
        )}
      >
        {count}
      </span>
      <span className="qc-sr-only">
        {count === 1 ? '1 documento' : `${count} documentos`}
        {pressed ? '. Filtro ativo.' : '.'}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ações por linha
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ações condicionadas às 6 FLAGS EFETIVAS que o servidor devolveu para aquela
 * linha. Nunca inferimos permissão no cliente: `row.permissions` é o resultado
 * de `resolveEffectivePermissions` executado no CORE.
 *
 * O legado renderizava Download e Editar incondicionalmente quando se filtrava
 * por pasta (BUG-60), o que produzia botões que sempre davam erro.
 */
function RowActions({ row }: { readonly row: MasterListRow }) {
  const { toast } = useToast();
  const canDownload = row.permissions[DocumentPermissionFlag.DOWNLOAD];
  const canWrite = row.permissions[DocumentPermissionFlag.WRITE];
  const canDecide = row.pendingMyApproval && row.permissions[DocumentPermissionFlag.APPROVE];
  const blockedByObsolescence =
    row.status === DocumentVersionStatus.OBSOLETE ||
    row.status === DocumentVersionStatus.SUPERSEDED;

  const download = async () => {
    try {
      const ticket = await documentsApi.download(row.documentId);
      window.open(ticket.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast({ tone: 'error', title: 'Download não autorizado', description: toUserMessage(error) });
    }
  };

  return (
    <TableActions label={`Ações para ${row.name}`}>
      <Button asChild size="icon" variant="ghost" iconLabel={`Abrir ${row.name}`}>
        <Link to="/documentos/$documentId" params={{ documentId: row.documentId }}>
          <IconEye size={16} />
        </Link>
      </Button>

      {/*
        R25/R26 do legado (`ged_list.twig:195-201`): a Lista Mestra oferecia a
        decisão de aprovação na própria linha. Aqui o atalho LEVA à tela do
        documento em vez de decidir na lista — a decisão é ato assinado
        (§11.50) e exige o diálogo de assinatura com o registro à vista. Um
        botão "Aprovar" que aprovasse direto da lista não teria como mostrar o
        que está sendo assinado.
      */}
      {canDecide ? (
        <Button
          asChild
          size="icon"
          variant="ghost"
          iconLabel={`Aprovar ou reprovar ${row.name} — abre o documento para assinatura`}
        >
          <Link to="/documentos/$documentId" params={{ documentId: row.documentId }}>
            <IconClipboardCheck size={16} className="text-warning-content" />
          </Link>
        </Button>
      ) : null}

      {canDownload ? (
        <Button
          size="icon"
          variant="ghost"
          iconLabel={
            blockedByObsolescence
              ? `Baixar ${row.name} — indisponível para documento obsoleto`
              : `Baixar ${row.name}`
          }
          disabled={blockedByObsolescence}
          onClick={() => void download()}
        >
          {blockedByObsolescence ? (
            <IconAlertTriangle size={16} className="text-content-subtle" />
          ) : (
            <IconDownload size={16} />
          )}
        </Button>
      ) : null}

      {canWrite ? (
        <Button asChild size="icon" variant="ghost" iconLabel={`Emitir nova versão de ${row.name}`}>
          <Link to="/documentos/$documentId/nova-versao" params={{ documentId: row.documentId }}>
            <IconEdit size={16} />
          </Link>
        </Button>
      ) : null}
    </TableActions>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────────────────────────

function toTreeNodes(nodes: readonly DocumentTypeNode[]): TreeNode[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.name,
    count: node.documentCount,
    disabled: !node.active,
    children: node.children.length > 0 ? toTreeNodes(node.children) : undefined,
    meta: node,
  }));
}

function countDocuments(node: DocumentTypeNode): number {
  return node.documentCount + node.children.reduce((acc, child) => acc + countDocuments(child), 0);
}

function countActiveFilters(filter: Partial<DocumentListFilter>): number {
  let count = 0;
  if (filter.documentTypeId) count += 1;
  if (filter.status && filter.status.length > 0) count += 1;
  if (filter.search) count += 1;
  if (typeof filter.expiringWithinDays === 'number') count += 1;
  if (filter.onlyPendingMyApproval) count += 1;
  if (filter.onlyPendingMyAcknowledgement) count += 1;
  return count;
}
